import * as React from "react";
import { useState } from "react";
import { Input } from "./input";
import { cn } from "../../lib/utils";

export interface NumericInputProps {
  id?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  maxLength?: number;
  step?: number;
  placeholder?: string;
  className?: string;
  hasError?: boolean;
  disabled?: boolean;
  /** Tipo de número: 'integer' ou 'decimal' */
  numberType?: "integer" | "decimal";
}

/**
 * NumericInput - Input numérico com validação de limites
 *
 * Características:
 * - Limita caracteres digitados (maxLength)
 * - Força valor dentro do range (min-max) no blur
 * - Permite campo vazio durante digitação (mostra placeholder)
 * - Suporta inteiros e decimais
 */
const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(
  (
    {
      id,
      value,
      onChange,
      min = 0,
      max = 99,
      maxLength = 2,
      step = 1,
      placeholder,
      className,
      hasError,
      disabled,
      numberType = "integer",
    },
    ref
  ) => {
    // Estado local para permitir campo vazio durante digitação
    const [localValue, setLocalValue] = useState<string>(
      value === 0 ? "" : String(value)
    );
    const [isFocused, setIsFocused] = useState(false);

    // Sincroniza com valor externo quando não está focado
    React.useEffect(() => {
      if (!isFocused) {
        setLocalValue(value === 0 ? "" : String(value));
      }
    }, [value, isFocused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Limita caracteres
      const truncatedValue = inputValue.slice(0, maxLength);
      setLocalValue(truncatedValue);

      // Parse baseado no tipo
      const parsedValue =
        numberType === "decimal"
          ? parseFloat(truncatedValue) || 0
          : parseInt(truncatedValue) || 0;

      // Aplica limites min/max
      const clampedValue = Math.min(max, Math.max(0, parsedValue));

      onChange(clampedValue);
    };

    const handleBlur = () => {
      setIsFocused(false);

      // No blur, aplica o min se valor for menor
      const parsedValue =
        numberType === "decimal"
          ? parseFloat(localValue) || 0
          : parseInt(localValue) || 0;

      const clampedValue = Math.min(max, Math.max(min, parsedValue));
      onChange(clampedValue);

      // Atualiza display
      setLocalValue(clampedValue === 0 ? "" : String(clampedValue));
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    return (
      <Input
        ref={ref}
        id={id}
        type="number"
        min={min}
        max={max}
        step={step}
        maxLength={maxLength}
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        hasError={hasError}
        disabled={disabled}
        className={cn("h-9", className)}
      />
    );
  }
);

NumericInput.displayName = "NumericInput";

export { NumericInput };
